import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import type { UserType } from "@/domain/core";

/**
 * Hook to determine user type based on the current path
 * @returns The user type determined from the current path
 */
export function useUserTypeFromPath(): UserType {
  const location = useLocation();

  const getUserType = useCallback((): UserType => {
    const path = location.pathname;

    if (path.startsWith("/admin")) return "admin";
    if (path.startsWith("/practice")) return "practice";
    if (path.startsWith("/professional")) return "professional";

    return "none";
  }, [location.pathname]);

  return getUserType();
}

/**
 * Hook to get the settings route based on the current path
 * @returns The appropriate settings route for the current user type
 */
export function useSettingsRoute(): string {
  const userType = useUserTypeFromPath();

  switch (userType) {
    case "admin":
    case "support":
      return "/admin/settings";
    case "practice":
      return "/practice/settings";
    case "professional":
      return "/professional/settings";
    default:
      return "/settings";
  }
}

/**
 * Marketplace profile pages exist only for practice and professional accounts.
 * The admin console (admin and support) has no profile route.
 */
export function getProfileRoute(userType: UserType | undefined): string | null {
  switch (userType) {
    case "practice":
      return "/practice/profile";
    case "professional":
      return "/professional/profile";
    default:
      return null;
  }
}

/**
 * Hook to get the profile route based on the current path
 * @returns The practice/professional profile route, or null for staff roles
 */
export function useProfileRoute(): string | null {
  return getProfileRoute(useUserTypeFromPath());
}

/**
 * Hook to get the messaging route based on the current path
 * @returns The appropriate messaging route for the current user type
 */
export function useMessagingRoute(): string {
  const userType = useUserTypeFromPath();

  switch (userType) {
    case "admin":
    case "support":
      return "/admin/messaging";
    case "practice":
      return "/practice/messaging";
    case "professional":
      return "/professional/messaging";
    default:
      return "/messaging";
  }
}

/**
 * Hook to get the notifications route based on the current path
 * @returns The appropriate notifications route for the current user type
 */
export function useNotificationsRoute(): string {
  const userType = useUserTypeFromPath();

  switch (userType) {
    case "admin":
    case "support":
      return "/admin/notifications";
    case "practice":
      return "/practice/notifications";
    case "professional":
      return "/professional/notifications";
    default:
      return "/notifications";
  }
}
